import {CLICK_SOUNDS, ClickSoundId} from "./sounds";

type ClickType = 'primary' | 'secondary' | 'silent';

class ClickPlayer {

    private readonly audioContext: AudioContext = new AudioContext();
    // we need this in the chain so that we can silence any scheduled clicks
    // when `stop()` is called
    private gainNode: GainNode;

    get currentTime(): number {
        return this.audioContext.currentTime;
    }

    private readonly buffers = new Map<ClickSoundId, AudioBuffer>();
    private primarySound: ClickSoundId;
    private secondarySound: ClickSoundId;
    private readonly silentBuffer: AudioBuffer;

    private readonly hasClickBuffers: Promise<unknown>;

    constructor(primarySound: ClickSoundId, secondarySound: ClickSoundId) {
        this.primarySound = primarySound;
        this.secondarySound = secondarySound;
        this.silentBuffer = this.audioContext.createBuffer(1, 1, 44100);
        this.hasClickBuffers = Promise.all(
            CLICK_SOUNDS.map(sound =>
                this.fetchWavAsBuffer(sound.file)
                    .then(buffer => this.buffers.set(sound.id, buffer))
            )
        );

        this.gainNode = new GainNode(this.audioContext)
        this.gainNode.connect(this.audioContext.destination)
    }

    async resume() {
        await this.audioContext
            .resume()
            .then(() => this.hasClickBuffers);
    }

    /**
     * Swap the sound used for downbeat clicks. Applies live, to clicks
     * scheduled from this point on.
     */
    setPrimarySound(sound: ClickSoundId) {
        this.primarySound = sound;
    }

    /**
     * Swap the sound used for non-downbeat clicks. Applies live, to clicks
     * scheduled from this point on.
     */
    setSecondarySound(sound: ClickSoundId) {
        this.secondarySound = sound;
    }

    /**
     * Play a sound immediately, connected straight to the destination -
     * bypassing the scheduled-click gain node, which is silenced on `stop()`.
     * Safe to call whether or not the metronome is playing.
     */
    async preview(sound: ClickSoundId) {
        await this.resume();
        const buffer = this.buffers.get(sound);
        if (!buffer) {
            return;
        }
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);
        source.start();
    }

    /**
     * Silence any already-scheduled audio
     */
    stop() {
        // reduce to silence
        this.gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime)
        this.gainNode.disconnect()

        // recreate
        this.gainNode = new GainNode(this.audioContext)
        this.gainNode.connect(this.audioContext.destination)
    }

    click(timestamp: number, clickType: ClickType) {
        const click = this.audioContext.createBufferSource();

        switch (clickType) {
            case 'primary': {
                const buffer = this.buffers.get(this.primarySound);
                if (buffer) {
                    click.buffer = buffer;
                }
                break;
            }
            case 'secondary': {
                const buffer = this.buffers.get(this.secondarySound);
                if (buffer) {
                    click.buffer = buffer;
                }
                break;
            }
            case "silent":
                click.buffer = this.silentBuffer;
                break;
        }

        click.connect(this.gainNode);
        click.start(timestamp);
    }

    private async fetchWavAsBuffer(url: string) {
        const response = await fetch(url);
        const arrayBuffer: ArrayBuffer = await response.arrayBuffer();
        return await this.audioContext.decodeAudioData(arrayBuffer);
    }
}

export {ClickPlayer, ClickType};
